import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Sort } from 'gql/autogen/Sort';
import { Matrix } from 'gql/autogen/Matrix';
import { SearchResponse } from 'gql/autogen/SearchResponse';
import { updateApiBaseState } from 'store/api/apiUtils';
import { ActivityGqlOperation, ApiBaseState, GqlApiResponse, OperationKey, SearchRequestProps } from 'store/api/api.types';
import { getFilterStateFromQueryParams, getSortStateFromQueryParams } from 'component/page/search/SortAndFilter/sortAndFilterQuery.utils';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { DefaultApiBaseState, DefaultRequestProps } from 'store/api/ApiConstants';
import { measureSearchApiResponse } from 'store/api/measurements/searchGqlMeasurements';
import { getSelectedActivityFromQueryParams } from 'gql/service/graph/activityService';
import { getPageNumberFromQueryParams } from 'component/page/search/Pagination/pagination.utils';
import { search as getLocationSearch } from 'core-framework/window/location';
import { removeItemFromLocalStorage } from 'core-framework/window/storage';
import { LocalStorageKey } from 'core-framework/constant/StorageKeys';
import { injectEnsightenTags } from 'core/messaging/analytics/ensightenTagsInjector';
import { EnsightenTagEventKey } from 'core/type/behaviors.types';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { FilterState } from 'component/common/Filter/Filter.types';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

export interface SearchState extends ApiBaseState {
    searchResponse?: SearchResponse;
    popularActivities?: ActivityCardProps[];
    sort: Sort;
    filter: FilterState;
    matrix: Matrix;
    pageNumber: number;
    selectedActivity?: number; // this field will Use for save selectedActivity from queryString only
    selectedTab?: string;
    isScrolling?: boolean;
}

export const searchInitialState: SearchState = {
    ...DefaultApiBaseState,
    searchResponse: undefined,
    sort: {
        code: SortCode.Default,
        order: SortOrder.Default,
    },
    filter: {
        rangeFilterMatrix: [],
        valueFilterMatrix: [],
    },
    matrix: {
        sort: {},
        filter: {},
    },
    selectedActivity: undefined,
    pageNumber: DefaultRequestProps.search.pagination.number,
    isScrolling: false,
};

let searchInitialStateLazy: SearchState;

const searchSlice = createSlice({
    name: 'search',
    initialState: (): SearchState => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!searchInitialStateLazy) {
            const search = getLocationSearch();
            const pageNumber = getPageNumberFromQueryParams(search);
            searchInitialStateLazy = { ...searchInitialState, ...DefaultApiBaseState, pageNumber };
        }
        return searchInitialStateLazy;
    },
    reducers: {
        getSearchInitialState: () => searchInitialState,
        searchLoadFinished: (state, action: PayloadAction<GqlApiResponse<SearchResponse, SearchRequestProps>>) => {
            const payload = action.payload;
            state.searchResponse = payload.result;
            updateApiBaseState(state, action);
            state.pageNumber = payload.props.pagination.number;
            if (state.isError || state.isSuccess) {
                // Keep the Categories and filters in Store until the result is loaded.
                // Stored data smoothens the transitions on UI.
                const result = payload.result?.result;
                state.matrix = result?.matrix || searchInitialState.matrix;
                measureSearchApiResponse(state.isSuccess, result?.activities?.length || 0, payload.pageTypeId, payload.config, state.error);

                const ensightenTagsConfig = payload.config.behavior.common.ensightenTagsConfiguration;
                if (state.isSuccess && ensightenTagsConfig.isEnabled) {
                    const ensightenEvents = ensightenTagsConfig.config?.events || {};
                    const ensightenEvent = ensightenEvents[EnsightenTagEventKey.SEARCH_RESULTS_PAGE_VIEW];
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    injectEnsightenTags(payload.config.geo?.name, result?.resultInfo?.totalActivities, ensightenEvent);
                }
            }
        },
        setSearchStateFromQueryParams: (state, action: PayloadAction<{ locationSearch: string; pageNumber: number }>) => {
            const sortState = getSortStateFromQueryParams(action.payload.locationSearch);
            const filterState = getFilterStateFromQueryParams(action.payload.locationSearch);
            const selectedActivity = getSelectedActivityFromQueryParams(action.payload.locationSearch);

            state.sort = { ...searchInitialState.sort, ...sortState };
            state.filter = { ...searchInitialState.filter, ...filterState };
            state.selectedActivity = selectedActivity;
            state.pageNumber = action.payload.pageNumber;
        },
        setSortAndFilter: (state, action: PayloadAction<{ sort: Sort; filter: FilterState }>) => {
            const { sort, filter } = action.payload;
            const isDefaultSort = sort.code === SortCode.Default;
            state.sort.code = isDefaultSort ? SortCode.Default : sort.code;
            state.sort.order = isDefaultSort ? SortOrder.Default : sort.order;
            state.filter.rangeFilterMatrix = filter.rangeFilterMatrix || [];
            state.filter.valueFilterMatrix = filter.valueFilterMatrix || [];
        },
        clearSortAndFilter: (state) => {
            const { sort, filter } = searchInitialState;
            state.sort.code = sort.code;
            state.sort.order = sort.order;
            state.filter.rangeFilterMatrix = filter.rangeFilterMatrix;
            state.filter.valueFilterMatrix = filter.valueFilterMatrix;
        },
        setSelectedTabOnSearchPage: (state, action: PayloadAction<string>) => {
            state.selectedTab = action.payload;
        },
        setIsScrolling: (state, action: PayloadAction<boolean>) => {
            // We will use this flag to prevent focus in order to not stop the scrolling
            state.isScrolling = action.payload;
        },
    },
});

export const fetchActivities = (pageNumber?: number, pageSize?: number) => {
    const args: { props: SearchRequestProps } = { props: DefaultRequestProps.search };

    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    if (pageNumber || pageSize) {
        const paginationProps = DefaultRequestProps.search.pagination;
        args.props = {
            searchType: args.props.searchType,
            searchRequestType: args.props.searchRequestType,
            pagination: {
                number: pageNumber || paginationProps.number,
                size: pageSize || paginationProps.size,
            },
            defaultSort: args.props.defaultSort,
        };
    }

    queryActivitiesGqlApi({ key: OperationKey.Search, operation: ActivityGqlOperation.search, args });
};

export const { getSearchInitialState, searchLoadFinished, setSearchStateFromQueryParams, setSortAndFilter, clearSortAndFilter, setSelectedTabOnSearchPage, setIsScrolling } =
    searchSlice.actions;

export default searchSlice.reducer;
